import { getInitData } from "./telegram";
import type { ErrorCode, GameStateView } from "@ludo/shared";

// Production worker URL — fallback when VITE_API_URL is not set at build time.
// (Change this if you fork the project with your own worker.)
export const API_BASE: string =
  (import.meta.env?.VITE_API_URL as string | undefined) ||
  "https://ludo-telegram.shomaletehran.workers.dev";

export class ApiError extends Error {
  code: ErrorCode;
  constructor(code: ErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `tma ${getInitData()}`,
      ...(options.headers ?? {}),
    },
  });
  const data = (await res.json()) as {
    ok: boolean;
    error?: { code: ErrorCode; message: string };
  } & T;
  if (!res.ok || !data.ok) {
    throw new ApiError(
      data.error?.code ?? ("INTERNAL_ERROR" as ErrorCode),
      data.error?.message ?? "خطای سرور",
    );
  }
  return data;
}

export interface MeResponse {
  user: {
    id: number;
    telegramId: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    avatarUrl: string | null;
    gamesPlayed: number;
    gamesWon: number;
    gamesLost: number;
    winRate: number;
    rating: number;
  };
  recentGames: Array<Record<string, unknown>>;
}

export const api = {
  auth: () => request<{ user: MeResponse["user"] }>("/api/auth/telegram", { method: "POST" }),
  createRoom: () =>
    request<{ roomCode: string; inviteLink: string | null; state: GameStateView }>(
      "/api/rooms",
      { method: "POST", body: "{}" },
    ),
  getRoom: (code: string) => request<{ state: GameStateView }>(`/api/rooms/${code}`),
  joinRoom: (code: string) =>
    request<{ state: GameStateView }>(`/api/rooms/${code}/join`, { method: "POST", body: "{}" }),
  startRoom: (code: string) =>
    request<{ state: GameStateView }>(`/api/rooms/${code}/start`, { method: "POST", body: "{}" }),
  leaveRoom: (code: string) =>
    request<Record<string, never>>(`/api/rooms/${code}/leave`, { method: "POST", body: "{}" }),
  me: () => request<MeResponse>("/api/users/me"),
  leaderboard: () =>
    request<{
      leaderboard: Array<{
        rank: number;
        username: string | null;
        firstName: string | null;
        games: number;
        wins: number;
        losses: number;
        winRate: number;
        rating: number;
      }>;
    }>("/api/leaderboard"),
  gameHistory: (gameId: number) =>
    request<{
      moves: Array<Record<string, unknown>>;
      turns: Array<Record<string, unknown>>;
      players: Array<Record<string, unknown>>;
    }>(`/api/games/${gameId}/history`),
  config: () =>
    request<{ botUsername: string | null; appShortName: string | null }>("/api/config"),
};

export function wsUrl(roomCode: string, lastVersion: number | null): string {
  const base = API_BASE || window.location.origin;
  const proto = base.startsWith("https") ? "wss" : "ws";
  const host = base.replace(/^https?:\/\//, "");
  const url = new URL(`${proto}://${host}/api/rooms/${roomCode}/ws`);
  if (lastVersion !== null) url.searchParams.set("v", String(lastVersion));
  return url.toString();
}
